/**
 * Contacts Screen - MOBILE ONLY
 * 
 * Address book for opportunity-related contacts.
 * Agents, sellers, inspectors, lenders, and other relevant parties.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, ScrollView, Pressable, StyleSheet, ActivityIndicator, Linking, Alert, Modal, TextInput, KeyboardAvoidingView, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, space, text, icon, radius, touch } from '../../../shared/tokens/mobile';
import { ScreenContainer, ScreenHeader } from '../../../components/v2/mobile';
import { authApi } from '../../../shared/utils/api';
import { logger } from '../../../shared/utils/logger';

interface OpportunityContact {
  id: number;
  name: string;
  role: string;
  company?: string;
  email?: string;
  phone?: string;
  notes?: string;
  created_at: string;
}

const ROLE_CONFIG: Record<string, { icon: string; color: string }> = {
  agent: { icon: 'person-outline', color: '#3B82F6' },
  seller: { icon: 'home-outline', color: '#22C55E' },
  lender: { icon: 'cash-outline', color: '#F59E0B' },
  inspector: { icon: 'search-outline', color: '#8B5CF6' },
  attorney: { icon: 'document-text-outline', color: '#EC4899' },
  surveyor: { icon: 'map-outline', color: '#14B8A6' },
  contractor: { icon: 'construct-outline', color: '#F97316' },
  neighbor: { icon: 'people-outline', color: '#6366F1' },
  other: { icon: 'ellipsis-horizontal-outline', color: '#6B7280' },
};

export default function ContactsScreen() {
  const router = useRouter();
  const { portfolioId, opportunityId, opportunityName } = useLocalSearchParams<{
    portfolioId: string;
    opportunityId: string;
    opportunityName?: string;
  }>();

  const handleBack = useCallback(() => router.back(), [router]);

  const [contacts, setContacts] = useState<OpportunityContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    if (!portfolioId || !opportunityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authApi.getPropertyContacts(
        parseInt(portfolioId, 10),
        parseInt(opportunityId, 10)
      );
      if (response.contacts) {
        // Map API contacts to our interface
        const mappedContacts: OpportunityContact[] = response.contacts.map((c: any) => {
          const name = [c.first_name, c.last_name].filter(Boolean).join(' ')
            || c.company_name || c.name || c.display_name || 'Unknown';
          return {
            id: c.id,
            name,
            role: c.contact_type?.name || c.role || c.contact_type || 'other',
            company: c.company_name || c.company,
            email: c.email,
            phone: c.phone || c.mobile,
            notes: c.notes,
            created_at: c.created_at,
          };
        });
        setContacts(mappedContacts);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load contacts';
      logger.error('Failed to fetch contacts', err, 'ContactsScreen');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, opportunityId]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Handle call
  const handleCall = useCallback((phone: string) => {
    Linking.openURL(`tel:${phone}`);
  }, []);

  // Handle email
  const handleEmail = useCallback((email: string) => {
    Linking.openURL(`mailto:${email}`);
  }, []);

  // Contact types from API
  const [contactTypes, setContactTypes] = useState<Array<{ id: number; name: string }>>([]);
  useEffect(() => {
    authApi.getContactTypes().then(res => {
      if (res.types) setContactTypes(res.types);
    }).catch(() => {});
  }, []);

  // Add contact modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ firstName: '', lastName: '', role: 'other', email: '', phone: '', company: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleAddContact = useCallback(() => {
    setAddForm({ firstName: '', lastName: '', role: 'other', email: '', phone: '', company: '', notes: '' });
    setShowAddModal(true);
  }, []);

  const handleSaveContact = useCallback(async () => {
    if (!addForm.firstName.trim() && !addForm.company.trim()) {
      Alert.alert('Required', 'Name or company is required');
      return;
    }
    if (!portfolioId || !opportunityId) return;
    setSaving(true);
    try {
      const typeMatch = contactTypes.find(t => t.name.toLowerCase() === addForm.role.toLowerCase());
      const contact_type_id = typeMatch?.id || contactTypes[0]?.id || 1;

      await authApi.createPropertyContact(
        parseInt(portfolioId, 10),
        parseInt(opportunityId, 10),
        {
          contact_type_id,
          first_name: addForm.firstName.trim() || undefined,
          last_name: addForm.lastName.trim() || undefined,
          company_name: addForm.company.trim() || undefined,
          email: addForm.email.trim() || undefined,
          phone: addForm.phone.trim() || undefined,
          notes: addForm.notes.trim() || undefined,
        }
      );
      setShowAddModal(false);
      await fetchContacts();
    } catch (err) {
      logger.error('Failed to create contact', err, 'ContactsScreen');
      Alert.alert('Error', 'Failed to add contact');
    } finally {
      setSaving(false);
    }
  }, [addForm, portfolioId, opportunityId, fetchContacts, contactTypes]);

  // Get role config with defensive handling
  const getRoleConfig = (role: string | undefined | null) => {
    if (!role) return ROLE_CONFIG.other;
    return ROLE_CONFIG[role.toLowerCase()] || ROLE_CONFIG.other;
  };

  // Loading state
  if (loading) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Contacts" onBack={handleBack} />
        <View style={styles.center}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </ScreenContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Contacts" onBack={handleBack} />
        <View style={styles.center}>
          <Ionicons name="alert-circle-outline" size={icon.xxl} color={color.error} />
          <Text style={styles.errorText}>{error}</Text>
          <Pressable onPress={fetchContacts} style={styles.retryButton}>
            <Text style={styles.retryButtonText}>Try Again</Text>
          </Pressable>
        </View>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <ScreenHeader title={opportunityName || 'Contacts'} onBack={handleBack} />

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {contacts.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="people-outline" size={icon.xxl} color={color.textMuted} />
            <Text style={styles.emptyTitle}>No contacts yet</Text>
            <Text style={styles.emptySubtitle}>Add agents, sellers, and other relevant contacts</Text>
            <Pressable style={styles.addButton} onPress={handleAddContact}>
              <Ionicons name="add" size={icon.md} color={color.surface} />
              <Text style={styles.addButtonText}>Add Contact</Text>
            </Pressable>
          </View>
        ) : (
          <>
            {/* CONTACTS LIST */}
            {contacts.map(contact => {
              const roleConfig = getRoleConfig(contact.role);
              return (
                <View key={contact.id} style={styles.contactCard}>
                  <View style={styles.contactHeader}>
                    <View style={[styles.roleIcon, { backgroundColor: roleConfig.color + '15' }]}>
                      <Ionicons name={roleConfig.icon as any} size={icon.md} color={roleConfig.color} />
                    </View>
                    <View style={styles.contactInfo}>
                      <Text style={styles.contactName}>{contact.name}</Text>
                      <Text style={styles.contactRole}>{contact.role}</Text>
                      {contact.company && <Text style={styles.contactCompany}>{contact.company}</Text>}
                    </View>
                  </View>

                  {/* ACTIONS */}
                  <View style={styles.contactActions}>
                    {contact.phone && (
                      <Pressable style={styles.actionButton} onPress={() => handleCall(contact.phone!)}>
                        <Ionicons name="call-outline" size={icon.md} color={color.primary} />
                        <Text style={styles.actionText}>Call</Text>
                      </Pressable>
                    )}
                    {contact.email && (
                      <Pressable style={styles.actionButton} onPress={() => handleEmail(contact.email!)}>
                        <Ionicons name="mail-outline" size={icon.md} color={color.primary} />
                        <Text style={styles.actionText}>Email</Text>
                      </Pressable>
                    )}
                  </View>

                  {/* NOTES */}
                  {contact.notes && (
                    <View style={styles.notesSection}>
                      <Text style={styles.notesLabel}>Notes</Text>
                      <Text style={styles.notesText}>{contact.notes}</Text>
                    </View>
                  )}
                </View>
              );
            })}

            {/* ADD BUTTON */}
            <Pressable style={styles.addContactRow} onPress={handleAddContact}>
              <Ionicons name="add-circle-outline" size={icon.lg} color={color.primary} />
              <Text style={styles.addContactText}>Add Contact</Text>
            </Pressable>
          </>
        )}
      </ScrollView>

      {/* ADD CONTACT MODAL */}
      <Modal visible={showAddModal} transparent animationType="slide" onRequestClose={() => setShowAddModal(false)}>
        <KeyboardAvoidingView style={styles.modalOverlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <Pressable style={styles.modalBackdrop} onPress={() => setShowAddModal(false)} />
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Add Contact</Text>

            <View style={{ flexDirection: 'row', gap: space.sm }}>
              <TextInput
                style={[styles.modalInput, { flex: 1 }]}
                placeholder="First Name"
                placeholderTextColor={color.textMuted}
                value={addForm.firstName}
                onChangeText={(v) => setAddForm(f => ({ ...f, firstName: v }))}
                autoFocus
              />
              <TextInput
                style={[styles.modalInput, { flex: 1 }]}
                placeholder="Last Name"
                placeholderTextColor={color.textMuted}
                value={addForm.lastName}
                onChangeText={(v) => setAddForm(f => ({ ...f, lastName: v }))}
              />
            </View>

            {/* Role Picker */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.rolePicker}>
              {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
                <Pressable
                  key={key}
                  style={[styles.roleChip, addForm.role === key && { backgroundColor: cfg.color + '20', borderColor: cfg.color }]}
                  onPress={() => setAddForm(f => ({ ...f, role: key }))}
                >
                  <Ionicons name={cfg.icon as any} size={14} color={addForm.role === key ? cfg.color : color.textMuted} />
                  <Text style={[styles.roleChipText, addForm.role === key && { color: cfg.color }]}>
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </Text>
                </Pressable>
              ))}
            </ScrollView>

            <TextInput
              style={styles.modalInput}
              placeholder="Email"
              placeholderTextColor={color.textMuted}
              value={addForm.email}
              onChangeText={(v) => setAddForm(f => ({ ...f, email: v }))}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <TextInput
              style={styles.modalInput}
              placeholder="Phone"
              placeholderTextColor={color.textMuted}
              value={addForm.phone}
              onChangeText={(v) => setAddForm(f => ({ ...f, phone: v }))}
              keyboardType="phone-pad"
            />
            <TextInput
              style={styles.modalInput}
              placeholder="Company"
              placeholderTextColor={color.textMuted}
              value={addForm.company}
              onChangeText={(v) => setAddForm(f => ({ ...f, company: v }))}
            />
            <TextInput
              style={[styles.modalInput, { minHeight: 60 }]}
              placeholder="Notes"
              placeholderTextColor={color.textMuted}
              value={addForm.notes}
              onChangeText={(v) => setAddForm(f => ({ ...f, notes: v }))}
              multiline
            />

            <View style={styles.modalButtons}>
              <Pressable style={styles.modalCancel} onPress={() => setShowAddModal(false)}>
                <Text style={styles.modalCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.modalSave, saving && { opacity: 0.6 }]}
                onPress={handleSaveContact}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={color.surface} />
                ) : (
                  <Text style={styles.modalSaveText}>Save Contact</Text>
                )}
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { padding: space.md },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: space.xl },
  errorText: { fontSize: text.body.fontSize, color: color.error, marginTop: space.md, textAlign: 'center' },
  retryButton: { marginTop: space.lg, paddingHorizontal: space.lg, paddingVertical: space.sm, backgroundColor: color.primary, borderRadius: radius.md },
  retryButtonText: { color: color.surface, fontWeight: text.bodyMedium.fontWeight },

  // Empty state
  emptyState: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: space.xxl },
  emptyTitle: { fontSize: text.cardTitle.fontSize, fontWeight: text.cardTitle.fontWeight, color: color.textPrimary, marginTop: space.md },
  emptySubtitle: { fontSize: text.body.fontSize, color: color.textMuted, marginTop: space.xs, textAlign: 'center' },
  addButton: { flexDirection: 'row', alignItems: 'center', gap: space.xs, marginTop: space.lg, paddingHorizontal: space.lg, paddingVertical: space.md, backgroundColor: color.primary, borderRadius: radius.md },
  addButtonText: { color: color.surface, fontWeight: text.bodyMedium.fontWeight, fontSize: text.body.fontSize },

  // Contact card
  contactCard: { backgroundColor: color.surface, borderRadius: radius.lg, padding: space.md, marginBottom: space.md, borderWidth: 1, borderColor: color.divider },
  contactHeader: { flexDirection: 'row', alignItems: 'flex-start' },
  roleIcon: { width: 44, height: 44, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center' },
  contactInfo: { flex: 1, marginLeft: space.md },
  contactName: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary },
  contactRole: { fontSize: text.caption.fontSize, color: color.primary, marginTop: 2, textTransform: 'capitalize' },
  contactCompany: { fontSize: text.caption.fontSize, color: color.textMuted, marginTop: 2 },

  // Actions
  contactActions: { flexDirection: 'row', gap: space.md, marginTop: space.md, paddingTop: space.md, borderTopWidth: 1, borderTopColor: color.divider },
  actionButton: { flexDirection: 'row', alignItems: 'center', gap: space.xs, paddingVertical: space.xs, paddingHorizontal: space.sm },
  actionText: { fontSize: text.caption.fontSize, color: color.primary, fontWeight: text.bodyMedium.fontWeight },

  // Notes
  notesSection: { marginTop: space.sm, paddingTop: space.sm, borderTopWidth: 1, borderTopColor: color.divider },
  notesLabel: { fontSize: text.caption.fontSize, color: color.textMuted, marginBottom: space.xs },
  notesText: { fontSize: text.body.fontSize, color: color.textPrimary },

  // Add contact row
  addContactRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: space.sm, paddingVertical: space.lg },
  addContactText: { fontSize: text.body.fontSize, color: color.primary, fontWeight: text.bodyMedium.fontWeight },

  // Modal
  modalOverlay: { flex: 1, justifyContent: 'flex-end' },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)' },
  modalCard: { backgroundColor: color.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: space.lg, paddingBottom: space.xxl, maxHeight: '85%' },
  modalTitle: { fontSize: text.sectionTitle.fontSize, fontWeight: text.sectionTitle.fontWeight, color: color.textPrimary, marginBottom: space.lg, textAlign: 'center' },
  modalInput: { backgroundColor: color.backgroundSecondary, borderRadius: radius.md, paddingHorizontal: space.md, paddingVertical: space.sm, fontSize: text.body.fontSize, color: color.textPrimary, marginBottom: space.sm, borderWidth: 1, borderColor: color.divider },
  rolePicker: { marginBottom: space.md },
  roleChip: { flexDirection: 'row', alignItems: 'center', gap: space.xs, paddingHorizontal: space.md, paddingVertical: space.sm, borderRadius: radius.full, marginRight: space.sm, borderWidth: 1, borderColor: color.divider, backgroundColor: color.backgroundSecondary },
  roleChipText: { fontSize: text.caption.fontSize, color: color.textMuted },
  modalButtons: { flexDirection: 'row', gap: space.md, marginTop: space.md },
  modalCancel: { flex: 1, paddingVertical: space.md, alignItems: 'center', borderRadius: radius.md, backgroundColor: color.backgroundSecondary },
  modalCancelText: { fontSize: text.body.fontSize, color: color.textMuted, fontWeight: text.bodyMedium.fontWeight },
  modalSave: { flex: 2, paddingVertical: space.md, alignItems: 'center', borderRadius: radius.md, backgroundColor: color.primary },
  modalSaveText: { fontSize: text.body.fontSize, color: color.surface, fontWeight: text.bodyMedium.fontWeight },
});

